import { useState, useEffect, useRef } from "react";
import "./../../../assets/css/student/CodeArena/CodeArena2.css";

/* ═══════════════════════════════════════════════════════════════
   STATIC DATA
═══════════════════════════════════════════════════════════════ */

const LANGUAGES = [
  { id:"c",      name:"C",              icon:"🔵", color:"#5c9ddc", desc:"Foundation Language"   },
  { id:"cpp",    name:"C++",            icon:"⚙️", color:"#00599c", desc:"Raw Performance"        },
  { id:"html",   name:"HTML",           icon:"🌐", color:"#e34c26", desc:"Web Structure"          },
  { id:"js",     name:"JavaScript",     icon:"⚡", color:"#f0db4f", desc:"Dynamic & Fast"         },
  { id:"react",  name:"React",          icon:"⚛️", color:"#61dafb", desc:"UI Framework"           },
  { id:"ds",     name:"Data Structures",icon:"🌲", color:"#10b981", desc:"Algorithms & Logic"     },
  { id:"oop",    name:"OOP",            icon:"🧩", color:"#9333ea", desc:"Object Oriented Design" },
];

// Topics per language — student selects what to practice
const TOPICS = {
  c: [
    { id:"basics",    name:"Basics & Syntax",   icon:"📝", desc:"Variables, data types, I/O" },
    { id:"pointers",  name:"Pointers",           icon:"🔗", desc:"Memory & pointer arithmetic" },
    { id:"functions", name:"Functions",           icon:"⚙️", desc:"Function calls, recursion"   },
    { id:"arrays",    name:"Arrays & Strings",   icon:"📦", desc:"1D, 2D arrays"               },
    { id:"structs",   name:"Structs & Unions",   icon:"🏗️", desc:"User-defined data types"     },
    { id:"files",     name:"File Handling",       icon:"📁", desc:"Read/Write file operations"  },
  ],
  cpp: [
    { id:"basics",    name:"Basics & Syntax",    icon:"📝", desc:"Variables, I/O, operators" },
    { id:"oop",       name:"OOP Concepts",        icon:"🧩", desc:"Classes, inheritance"       },
    { id:"stl",       name:"STL",                 icon:"📚", desc:"Vectors, maps, sets"        },
    { id:"templates", name:"Templates",           icon:"🔄", desc:"Generic programming"        },
    { id:"pointers",  name:"Pointers & Refs",    icon:"🔗", desc:"Memory management"          },
    { id:"excep",     name:"Exception Handling",  icon:"⚠️", desc:"Try, catch, throw"         },
  ],
  html: [
    { id:"tags",      name:"HTML Tags",           icon:"🏷️", desc:"Basic structure & tags"     },
    { id:"forms",     name:"Forms & Inputs",      icon:"📋", desc:"Forms, validation"          },
    { id:"semantic",  name:"Semantic HTML",       icon:"🔖", desc:"Header, section, article"   },
    { id:"tables",    name:"Tables",              icon:"📊", desc:"Table structure & styling"  },
    { id:"media",     name:"Media Elements",      icon:"🎬", desc:"Images, video, audio"       },
    { id:"meta",      name:"Meta & SEO",          icon:"🔍", desc:"Meta tags, SEO basics"      },
  ],
  js: [
    { id:"basics",    name:"Basics & Syntax",    icon:"📝", desc:"Variables, loops, types"    },
    { id:"functions", name:"Functions & Scope",  icon:"⚙️", desc:"Closures, arrow functions"  },
    { id:"dom",       name:"DOM Manipulation",   icon:"🌐", desc:"Selectors, events"          },
    { id:"async",     name:"Async & Promises",   icon:"⏳", desc:"Async/await, fetch API"     },
    { id:"arrays",    name:"Arrays & Objects",   icon:"📦", desc:"Map, filter, reduce"        },
    { id:"es6",       name:"ES6+ Features",       icon:"✨", desc:"Destructuring, spread"      },
  ],
  react: [
    { id:"components",name:"Components",         icon:"🧱", desc:"Functional components"      },
    { id:"hooks",     name:"Hooks",               icon:"🎣", desc:"useState, useEffect"        },
    { id:"props",     name:"Props & State",       icon:"📨", desc:"Data flow, lifting state"   },
    { id:"router",    name:"React Router",        icon:"🗺️", desc:"Navigation & routing"       },
    { id:"context",   name:"Context API",         icon:"🔗", desc:"Global state management"   },
    { id:"perf",      name:"Performance",         icon:"⚡", desc:"Memo, useMemo, lazy"        },
  ],
  ds: [
    { id:"arrays",    name:"Arrays & Lists",     icon:"📦", desc:"Linear data structures"     },
    { id:"trees",     name:"Trees & Graphs",     icon:"🌲", desc:"BST, BFS, DFS"              },
    { id:"sorting",   name:"Sorting Algorithms", icon:"🔢", desc:"Quick, merge, bubble"       },
    { id:"searching", name:"Searching",          icon:"🔍", desc:"Binary search, hashing"     },
    { id:"stack",     name:"Stack & Queue",      icon:"📚", desc:"LIFO, FIFO structures"      },
    { id:"dp",        name:"Dynamic Programming",icon:"🧮", desc:"Memoization, tabulation"    },
  ],
  oop: [
    { id:"classes",   name:"Classes & Objects",  icon:"🏛️", desc:"Blueprints & instances"     },
    { id:"inherit",   name:"Inheritance",        icon:"🔗", desc:"Parent-child relationships"  },
    { id:"poly",      name:"Polymorphism",       icon:"🔄", desc:"Method overriding"           },
    { id:"encap",     name:"Encapsulation",      icon:"🔒", desc:"Access modifiers"            },
    { id:"abstract",  name:"Abstraction",        icon:"🎭", desc:"Abstract classes, interfaces"},
    { id:"solid",     name:"SOLID Principles",   icon:"🏗️", desc:"Design best practices"      },
  ],
};

// Questions per language+topic
const QUESTIONS_DB = {
  c_basics: [
    { q:"What is the correct way to declare an integer in C?", code:`___ x = 5;`, opts:["int","integer","number","var"], ans:0 },
    { q:"Which format specifier is used for integer in printf?", code:`printf("___ ", x);`, opts:["%d","%s","%f","%c"], ans:0 },
    { q:"What is the size of int in C (typically)?", code:`sizeof(int);`, opts:["2 bytes","4 bytes","8 bytes","1 byte"], ans:1 },
    { q:"Which header file is required for printf?", code:`#include <___>`, opts:["stdlib.h","stdio.h","string.h","math.h"], ans:1 },
    { q:"What does \\n do in C?", code:`printf("Hello\\n");`, opts:["Tab","New line","Space","Null"], ans:1 },
  ],
  c_pointers: [
    { q:"How do you declare a pointer to integer?", code:`___ ptr;`, opts:["int ptr","int *ptr","*int ptr","ptr int"], ans:1 },
    { q:"What does & operator do?", code:`int x = 5;\nint *p = &x;`, opts:["Value of x","Address of x","Pointer to x","Size of x"], ans:1 },
    { q:"What does * (dereference) operator do?", code:`int val = *p;`, opts:["Address","Name","Value at address","Size"], ans:2 },
    { q:"What is a NULL pointer?", code:`int *p = NULL;`, opts:["Points to 0","Points nowhere","Points to memory","Error"], ans:1 },
    { q:"What is pointer arithmetic?", code:`p++;`, opts:["Increment value","Move to next memory","Division","None"], ans:1 },
  ],
  cpp_oop: [
    { q:"Which keyword defines a class in C++?", code:`___ Animal { };`, opts:["struct","class","object","type"], ans:1 },
    { q:"What is the default access modifier in a class?", code:`class Dog {\n  int age;\n};`, opts:["public","private","protected","none"], ans:1 },
    { q:"What is a constructor?", code:`Dog() { }`, opts:["Destructor","Function to init object","Variable","Operator"], ans:1 },
    { q:"Which symbol is used for scope resolution?", code:`Dog::bark()`, opts:["->","::",".",":"], ans:1 },
    { q:"What does 'this' keyword refer to?", code:`this->name = name;`, opts:["Parent class","Current object","Static member","Global var"], ans:1 },
  ],
  cpp_basics: [
    { q:"What is the output of cout << 10/3?", code:`cout << 10/3;`, opts:["3.33","3","4","Error"], ans:1 },
    { q:"Which is correct way to take input?", code:`___ >> x;`, opts:["cout","cin","printf","scanf"], ans:1 },
    { q:"What does :: mean in C++?", code:`std::cout << "hi";`, opts:["Pointer","Scope resolution","Inheritance","Friend"], ans:1 },
    { q:"What is the difference between '=' and '=='?", code:`if (x == 5)`, opts:["No difference","Assignment vs Comparison","Both compare","Both assign"], ans:1 },
    { q:"Which loop guarantees at least one execution?", code:`do { } while(x > 0);`, opts:["for","while","do-while","foreach"], ans:2 },
  ],
  html_tags: [
    { q:"Which tag creates a hyperlink?", code:`<___  href='url'>Click</___>`, opts:["link","a","href","url"], ans:1 },
    { q:"Which tag is for largest heading?", code:`<___>Big Title</___>`, opts:["h6","h3","h1","heading"], ans:2 },
    { q:"Which tag creates an unordered list?", code:`<___>\n  <li>Item</li>\n</___>`, opts:["ol","ul","li","list"], ans:1 },
    { q:"Which attribute specifies image source?", code:`<img ___='pic.jpg'>`, opts:["href","link","src","url"], ans:2 },
    { q:"What does <br> tag do?", code:`Line1<br>Line2`, opts:["Bold","Break/new line","Border","Background"], ans:1 },
  ],
  js_basics: [
    { q:"What is the output of typeof null?", code:`console.log(typeof null);`, opts:["null","undefined","object","string"], ans:2 },
    { q:"Which method removes last array element?", code:`arr.___();`, opts:["shift()","pop()","splice()","slice()"], ans:1 },
    { q:"What does === check?", code:`1 === '1'`, opts:["Value only","Type only","Both value & type","Neither"], ans:2 },
    { q:"What is the output?", code:`console.log(0 == false);`, opts:["false","true","error","undefined"], ans:1 },
    { q:"How do you declare a constant?", code:`___ PI = 3.14;`, opts:["var","let","const","fixed"], ans:2 },
  ],
  js_functions: [
    { q:"What is an arrow function?", code:`const greet = () => 'Hello';`, opts:["Normal function","Shorter function syntax","Class method","Constructor"], ans:1 },
    { q:"What does 'return' do in a function?", code:`function add(a,b){\n  return a+b;\n}`, opts:["Prints value","Sends value back","Loops","None"], ans:1 },
    { q:"What is a closure?", code:`function outer(){\n  let x=5;\n  return ()=>x;\n}`, opts:["Error","Function using outer scope","Class","Module"], ans:1 },
    { q:"What does the 'arguments' object hold?", code:`function f(){\n  console.log(arguments);\n}`, opts:["No params","All passed arguments","Errors","Globals"], ans:1 },
    { q:"What is a callback function?", code:`setTimeout(callback, 1000)`, opts:["A loop","Function passed as argument","Class","Event"], ans:1 },
  ],
  react_hooks: [
    { q:"Which hook manages local state?", code:`const [count, setCount] = ___()`, opts:["useRef","useEffect","useState","useContext"], ans:2 },
    { q:"When does useEffect run?", code:`useEffect(() => {}, [dep])`, opts:["On every render","Only once","When dep changes","On unmount"], ans:2 },
    { q:"What does useRef return?", code:`const ref = useRef(null)`, opts:["State","Mutable ref object","Context","Reducer"], ans:1 },
    { q:"How do you update state correctly?", code:`setCount(count + 1)`, opts:["count = count+1","count++","setCount(count+1)","update(count)"], ans:2 },
    { q:"Which hook subscribes to context?", code:`const value = ___(MyContext)`, opts:["useState","useEffect","useContext","useReducer"], ans:2 },
  ],
  ds_sorting: [
    { q:"What is the time complexity of Bubble Sort?", code:`// Best case?`, opts:["O(n log n)","O(n²)","O(n)","O(1)"], ans:1 },
    { q:"Which sort algorithm has best average O(n log n)?", code:`// Compare sorts`, opts:["Bubble","Selection","Quick Sort","Insertion"], ans:2 },
    { q:"What is the space complexity of Merge Sort?", code:`// Extra space?`, opts:["O(1)","O(n)","O(log n)","O(n²)"], ans:1 },
    { q:"Which algorithm is in-place?", code:`// No extra memory?`, opts:["Merge Sort","Counting Sort","Heap Sort","Radix Sort"], ans:2 },
    { q:"Best case of Insertion Sort?", code:`// Already sorted?`, opts:["O(n²)","O(n log n)","O(n)","O(1)"], ans:2 },
  ],
  ds_trees: [
    { q:"In BST, where is the smallest value?", code:`// Binary Search Tree`, opts:["Root","Right leaf","Left-most node","Middle"], ans:2 },
    { q:"What is the height of a single node tree?", code:`// Just root`, opts:["0","1","-1","2"], ans:0 },
    { q:"Which traversal visits root first?", code:`// Root → Left → Right`, opts:["Inorder","Postorder","Preorder","Levelorder"], ans:2 },
    { q:"What does BFS use internally?", code:`// Breadth First Search`, opts:["Stack","Queue","Array","Tree"], ans:1 },
    { q:"Time complexity of BST search (balanced)?", code:`// Balanced BST`, opts:["O(n)","O(1)","O(log n)","O(n²)"], ans:2 },
  ],
  oop_classes: [
    { q:"What is an object?", code:`Dog myDog = new Dog();`, opts:["Blueprint","Instance of a class","Function","Variable"], ans:1 },
    { q:"What is a class?", code:`class Dog { }`, opts:["Object","Blueprint/template","Function","Variable"], ans:1 },
    { q:"What is encapsulation?", code:`private int age;`, opts:["Inheritance","Hiding data","Polymorphism","Abstraction"], ans:1 },
    { q:"What is method overriding?", code:`@Override\nvoid sound() { }`, opts:["New method","Redefine parent method","Static method","Constructor"], ans:1 },
    { q:"Which keyword is used for inheritance?", code:`class Dog ___ Animal { }`, opts:["extends","implements","inherits","uses"], ans:0 },
  ],
  oop_inherit: [
    { q:"What does 'super' keyword do?", code:`super.method()`, opts:["Calls child","Calls parent","Static call","Interface"], ans:1 },
    { q:"What is multiple inheritance?", code:`// C++ supports it`, opts:["One parent","Two+ parents","No parent","Interface only"], ans:1 },
    { q:"What is the base class also called?", code:`class Animal { } // ?`, opts:["Derived","Child","Parent/Super class","Abstract"], ans:2 },
    { q:"What is an abstract class?", code:`abstract class Shape { }`, opts:["Can be instantiated","Cannot be instantiated","Has no methods","Interface"], ans:1 },
    { q:"What does final keyword prevent?", code:`final class Dog { }`, opts:["Inheritance","Encapsulation","Abstraction","Polymorphism"], ans:0 },
  ],
};

// Get questions for selected lang+topic
function getQuestions(langId, topicId) {
  const key = `${langId}_${topicId}`;
  return QUESTIONS_DB[key] || QUESTIONS_DB["js_basics"];
}

// Pakistani student leaderboard (simulated logged-in users)
const LEADERBOARD_STUDENTS = [
  { rank:1, name:"Ali Hassan",      pts:9850, avatar:"👾", badge:"🥇", wins:47, city:"Lahore",    online:true  },
  { rank:2, name:"Fatima Malik",    pts:8720, avatar:"🤖", badge:"🥈", wins:39, city:"Karachi",   online:true  },
  { rank:3, name:"Usman Ahmed",     pts:7600, avatar:"🦊", badge:"🥉", wins:31, city:"Islamabad", online:false },
  { rank:4, name:"Ayesha Khan",     pts:6540, avatar:"🐉", badge:"",   wins:24, city:"Lahore",    online:true  },
  { rank:5, name:"Bilal Raza",      pts:5980, avatar:"⚡", badge:"",   wins:21, city:"Multan",    online:true  },
  { rank:6, name:"Sara Noor",       pts:5320, avatar:"🎭", badge:"",   wins:18, city:"Faisalabad", online:false },
  { rank:7, name:"Hamza Sheikh",    pts:4750, avatar:"🌀", badge:"",   wins:15, city:"Rawalpindi", online:true  },
  { rank:8, name:"Zainab Hussain",  pts:4120, avatar:"👻", badge:"",   wins:12, city:"Peshawar",  online:false },
  { rank:9, name:"Omar Farooq",     pts:3890, avatar:"🎯", badge:"",   wins:10, city:"Lahore",    online:true  },
  { rank:10,name:"Maryam Iqbal",    pts:3450, avatar:"⭐", badge:"",   wins:8,  city:"Karachi",   online:true  },
];

const DAILY = {
  title:"Array Rotation Challenge",
  difficulty:"HARD",
  reward:500,
  desc:"Given an array of n elements, rotate it to the right by k steps in-place. Optimize for O(1) extra space.",
};

const PLAYER = { name:"PLAYER_ONE", level:23, xp:68, score:4280, hp:100 };

/* ═══════════════════════════════════════════════════════════════
   PARTICLE CANVAS
═══════════════════════════════════════════════════════════════ */
function ParticleField() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    const ctx = c.getContext("2d");
    c.width = window.innerWidth; c.height = window.innerHeight;
    const colors = ["rgba(0,212,255,", "rgba(147,51,234,", "rgba(16,185,129,"];
    const pts = Array.from({ length: 70 }, () => ({
      x: Math.random() * c.width, y: Math.random() * c.height,
      vx: (Math.random() - 0.5) * 0.45, vy: (Math.random() - 0.5) * 0.45,
      r: Math.random() * 1.8 + 0.4,
      col: colors[Math.floor(Math.random() * 3)],
    }));
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      pts.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > c.width)  p.vx *= -1;
        if (p.y < 0 || p.y > c.height) p.vy *= -1;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.col + "0.7)"; ctx.shadowBlur = 7;
        ctx.shadowColor = p.col + "1)"; ctx.fill();
      });
      for (let i = 0; i < pts.length; i++)
        for (let j = i + 1; j < pts.length; j++) {
          const d = Math.hypot(pts[i].x - pts[j].x, pts[i].y - pts[j].y);
          if (d < 95) {
            ctx.beginPath(); ctx.moveTo(pts[i].x, pts[i].y); ctx.lineTo(pts[j].x, pts[j].y);
            ctx.strokeStyle = `rgba(0,212,255,${0.1 * (1 - d / 95)})`; ctx.lineWidth = 0.4; ctx.stroke();
          }
        }
      raf = requestAnimationFrame(draw);
    };
    draw();
    const resize = () => { c.width = window.innerWidth; c.height = window.innerHeight; };
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} style={{ position:"fixed", top:0, left:0, zIndex:0, pointerEvents:"none" }} />;
}

/* ═══════════════════════════════════════════════════════════════
   SVG PLAYER CHARACTER
═══════════════════════════════════════════════════════════════ */
function PlayerCharacter({ size = 160, color = "#00d4ff", animate = true, label = "PLAYER_ONE", showLabel = true }) {
  const c      = color;
  const shadow = color === "#ef4444" ? "rgba(239,68,68,0.7)" : `${color}b3`;
  const visorC = color === "#ef4444" ? "#ff4444" : "#00d4ff";
  const gradId = `bg_${color.replace("#", "")}`;
  return (
    <div className="player-char-wrap" style={{ width: size }}>
      <div className="char-aura" style={{ background:`radial-gradient(ellipse,${shadow} 0%,transparent 70%)`, width:size*0.6 }} />
      <svg width={size} height={size*1.45} viewBox="0 0 100 145"
        style={{ filter:`drop-shadow(0 0 8px ${shadow}) drop-shadow(0 0 18px ${shadow})`, animation:animate?"charIdle 3s ease-in-out infinite":"none" }}
        xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id={gradId} cx="50%" cy="30%" r="60%">
            <stop offset="0%" stopColor={color} stopOpacity="0.25"/>
            <stop offset="100%" stopColor="#000" stopOpacity="0"/>
          </radialGradient>
        </defs>
        <ellipse cx="50" cy="142" rx="28" ry="4" fill={shadow} opacity="0.35"/>
        {/* Legs */}
        <rect x="33" y="98"  width="13" height="24" rx="3"   fill="#0a1628" stroke={c} strokeWidth="1.2"/>
        <rect x="33" y="116" width="13" height="6"  rx="1"   fill={c} opacity="0.7"/>
        <rect x="30" y="118" width="17" height="8"  rx="2"   fill="#0d1f3c" stroke={c} strokeWidth="1"/>
        <rect x="28" y="122" width="19" height="4"  rx="1.5" fill={c} opacity="0.5"/>
        <rect x="54" y="98"  width="13" height="24" rx="3"   fill="#0a1628" stroke={c} strokeWidth="1.2"/>
        <rect x="54" y="116" width="13" height="6"  rx="1"   fill={c} opacity="0.7"/>
        <rect x="53" y="118" width="17" height="8"  rx="2"   fill="#0d1f3c" stroke={c} strokeWidth="1"/>
        <rect x="53" y="122" width="19" height="4"  rx="1.5" fill={c} opacity="0.5"/>
        {/* Torso */}
        <rect x="29" y="54" width="42" height="46" rx="5" fill="#0a1628" stroke={c} strokeWidth="1.5"/>
        <rect x="33" y="58" width="34" height="28" rx="3" fill="#0d1f3c" stroke={c} strokeWidth="1"/>
        <line x1="50" y1="62" x2="50" y2="82" stroke={c} strokeWidth="0.8" opacity="0.8"/>
        <line x1="38" y1="70" x2="62" y2="70" stroke={c} strokeWidth="0.6" opacity="0.5"/>
        <polygon points="50,63 55,66 55,72 50,75 45,72 45,66" fill="none" stroke={c} strokeWidth="1" opacity="0.9"/>
        <circle cx="50" cy="69" r="2.5" fill={c} opacity="0.8"/>
        <rect x="29" y="92" width="42" height="8"  rx="2" fill="#0d1f3c" stroke={c} strokeWidth="1"/>
        <rect x="46" y="93" width="8"  height="6"  rx="1" fill={c} opacity="0.7"/>
        {/* Shoulders */}
        <rect x="20" y="52" width="14" height="10" rx="3" fill="#0d1f3c" stroke={c} strokeWidth="1.2"/>
        <rect x="66" y="52" width="14" height="10" rx="3" fill="#0d1f3c" stroke={c} strokeWidth="1.2"/>
        <rect x="20" y="52" width="14" height="4"  rx="2" fill={c} opacity="0.6"/>
        <rect x="66" y="52" width="14" height="4"  rx="2" fill={c} opacity="0.6"/>
        {/* Arms */}
        <rect x="18" y="60" width="12" height="30" rx="4" fill="#0a1628" stroke={c} strokeWidth="1.2"/>
        <rect x="19" y="80" width="10" height="8"  rx="2" fill={c} opacity="0.5"/>
        <rect x="16" y="87" width="14" height="10" rx="3" fill="#0d1f3c" stroke={c} strokeWidth="1.2"/>
        <rect x="16" y="87" width="14" height="4"  rx="2" fill={c} opacity="0.7"/>
        <rect x="70" y="60" width="12" height="30" rx="4" fill="#0a1628" stroke={c} strokeWidth="1.2"/>
        <rect x="71" y="80" width="10" height="8"  rx="2" fill={c} opacity="0.5"/>
        <rect x="70" y="87" width="14" height="10" rx="3" fill="#0d1f3c" stroke={c} strokeWidth="1.2"/>
        <rect x="70" y="87" width="14" height="4"  rx="2" fill={c} opacity="0.7"/>
        {/* Weapon */}
        <rect x="80" y="88" width="5"  height="22" rx="2"   fill="#0d1f3c" stroke={c} strokeWidth="1"/>
        <rect x="81" y="90" width="3"  height="18" rx="1.5" fill={c} opacity="0.6"/>
        <rect x="81" y="90" width="3"  height="10" rx="1.5" fill="white" opacity="0.4"/>
        <polygon points="81.5,88 83.5,88 82.5,82" fill={c} opacity="0.9"/>
        {/* Neck */}
        <rect x="43" y="44" width="14" height="12" rx="3" fill="#0a1628" stroke={c} strokeWidth="1"/>
        <rect x="46" y="45" width="8"  height="5"  rx="1" fill={c} opacity="0.4"/>
        {/* Helmet */}
        <rect x="32" y="16" width="36" height="32" rx="8" fill="#0a1628" stroke={c} strokeWidth="1.8"/>
        <rect x="40" y="12" width="20" height="7"  rx="3" fill="#0d1f3c" stroke={c} strokeWidth="1.2"/>
        <rect x="47" y="8"  width="6"  height="6"  rx="2" fill={c} opacity="0.8"/>
        <line x1="50" y1="8" x2="50" y2="2" stroke={c} strokeWidth="1.2"/>
        <circle cx="50" cy="2" r="1.8" fill={c}/>
        {/* Visor */}
        <rect x="36" y="25" width="28" height="12" rx="4" fill={visorC} opacity="0.15" stroke={visorC} strokeWidth="1.2"/>
        <rect x="36" y="30" width="28" height="2"  rx="1" fill={visorC} opacity="0.35"/>
        <rect x="39" y="27" width="8"  height="5"  rx="2" fill={visorC} opacity="0.9"/>
        <rect x="53" y="27" width="8"  height="5"  rx="2" fill={visorC} opacity="0.9"/>
        <circle cx="43" cy="29.5" r="1.5" fill="white" opacity="0.8"/>
        <circle cx="57" cy="29.5" r="1.5" fill="white" opacity="0.8"/>
        <rect x="38" y="40" width="24" height="7"  rx="3" fill="#0d1f3c" stroke={c} strokeWidth="1"/>
        <rect x="44" y="41" width="12" height="3"  rx="1" fill={c} opacity="0.4"/>
        <rect x="28" y="22" width="7"  height="14" rx="3" fill="#0d1f3c" stroke={c} strokeWidth="1"/>
        <rect x="65" y="22" width="7"  height="14" rx="3" fill="#0d1f3c" stroke={c} strokeWidth="1"/>
        <rect x="30" y="25" width="3"  height="5"  rx="1" fill={c} opacity="0.7"/>
        <rect x="67" y="25" width="3"  height="5"  rx="1" fill={c} opacity="0.7"/>
        <rect x="29" y="54" width="42" height="46" rx="5" fill={`url(#${gradId})`} opacity="0.7"/>
        <line x1="36" y1="100" x2="36" y2="115" stroke={c} strokeWidth="0.6" opacity="0.5"/>
        <line x1="57" y1="100" x2="57" y2="115" stroke={c} strokeWidth="0.6" opacity="0.5"/>
        <line x1="34" y1="108" x2="44" y2="108" stroke={c} strokeWidth="0.5" opacity="0.4"/>
        <line x1="56" y1="108" x2="66" y2="108" stroke={c} strokeWidth="0.5" opacity="0.4"/>
      </svg>
      {showLabel && <div className="char-name" style={{ color:c, textShadow:`0 0 8px ${c}` }}>{label}</div>}
    </div>
  );
}

/* ─── Shared Helpers ──────────────────────────────────────── */
function StatBar({ label, value, max = 100, colorCls = "stat-bar-fill--xp" }) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div className="stat-bar-wrap">
      <div className="stat-bar-label"><span>{label}</span><span>{value}/{max}</span></div>
      <div className="stat-bar-track"><div className={`stat-bar-fill ${colorCls}`} style={{ width:`${pct}%` }}/></div>
    </div>
  );
}

function GlitchText({ text, size = "clamp(2rem,6vw,4.5rem)" }) {
  return (
    <div className="glitch-wrap">
      <span className="glitch-main" style={{ fontSize:size }}>{text}</span>
      <span className="glitch-a"    style={{ fontSize:size }}>{text}</span>
      <span className="glitch-b"    style={{ fontSize:size }}>{text}</span>
    </div>
  );
}

function Btn({ children, onClick, color = "blue", size = "md", disabled = false, full = false }) {
  const cls = ["nbtn", `nbtn--${color}`, `nbtn--${size}`, full ? "nbtn--full" : ""].filter(Boolean).join(" ");
  return <button className={cls} onClick={onClick} disabled={disabled}>{children}</button>;
}

function useCountdown(start, onEnd) {
  const [t, setT] = useState(start);
  useEffect(() => {
    setT(start); if (start <= 0) return;
    const iv = setInterval(() => {
      setT(p => { if (p <= 1) { clearInterval(iv); onEnd?.(); return 0; } return p - 1; });
    }, 1000);
    return () => clearInterval(iv);
  }, [start]);
  return t;
}

/* ═══════════════════════════════════════════════════════════════
   SCREEN: LOBBY
═══════════════════════════════════════════════════════════════ */
function LobbyScreen({ onNav, player }) {
  const menu = [
    { label:"▶  START GAME",       icon:"🎮", color:"blue",   action:"language",    desc:"Quick Match"    },
    { label:"☀  DAILY CHALLENGE",  icon:"🔥", color:"orange", action:"daily",       desc:"500 pts reward" },
    { label:"⚔  1 VS 1 BATTLE",    icon:"⚡", color:"purple", action:"leaderboard", desc:"Challenge a player" },
    { label:"🏆  LEADERBOARD",     icon:"🥇", color:"green",  action:"leaderboard", desc:"Top Players"    },
  ];
  const borderMap = { blue:"rgba(0,212,255,0.3)", orange:"rgba(245,158,11,0.3)", purple:"rgba(147,51,234,0.3)", green:"rgba(16,185,129,0.3)" };
  const bgMap     = { blue:"rgba(0,212,255,0.06)", orange:"rgba(245,158,11,0.06)", purple:"rgba(147,51,234,0.06)", green:"rgba(16,185,129,0.06)" };
  const textMap   = { blue:"#00d4ff", orange:"#f59e0b", purple:"#9333ea", green:"#10b981" };

  return (
    <div className="screen">
      <div className="lobby-layout">
        {/* Left: Player Panel */}
        <div className="hud-panel player-sidebar anim-slide-left">
          <div className="player-sidebar__level">LVL {player.level}</div>
          <PlayerCharacter size={130} color="#00d4ff" label={player.name} animate />
          <StatBar label="XP" value={player.xp} colorCls="stat-bar-fill--xp" max={100}/>
          <StatBar label="HP" value={player.hp} colorCls="stat-bar-fill--hp" max={100}/>
          <div style={{ textAlign:"center" }}>
            <div style={{ fontSize:"0.55rem", fontFamily:"var(--f-mono)", color:"rgba(255,255,255,0.4)", letterSpacing:"2px", marginBottom:"2px" }}>SCORE</div>
            <div className="player-sidebar__score">⭐ {player.score.toLocaleString()}</div>
          </div>
          <Btn onClick={() => onNav("language")} color="green" size="sm">SELECT LANG</Btn>
        </div>

        {/* Center */}
        <div className="lobby-center">
          <div className="lobby-title-wrap anim-slide-up">
            <div className="lobby-sub">◈ ENTER THE ARENA ◈</div>
            <GlitchText text="CODE ARENA"/>
            <div className="lobby-tag">COMPETE · CODE · CONQUER</div>
          </div>
          <div className="lobby-menu anim-up-d2">
            {menu.map(b => (
              <button key={b.action + b.label} className="lobby-menu-btn" onClick={() => onNav(b.action)} style={{
                background:`linear-gradient(135deg,rgba(0,0,0,0.88),${bgMap[b.color]})`,
                border:`1px solid ${borderMap[b.color]}`, boxShadow:`0 0 16px ${bgMap[b.color]}`,
              }}>
                <span className="lobby-menu-btn__icon">{b.icon}</span>
                <span className="lobby-menu-btn__text">
                  <div className="lobby-menu-btn__label" style={{ color:textMap[b.color] }}>{b.label}</div>
                  <div className="lobby-menu-btn__desc">{b.desc}</div>
                </span>
              </button>
            ))}
          </div>
          <div className="live-stats anim-up-d4">
            {[["ONLINE","1,247"],["MATCHES","8,932"],["EVENTS","3 LIVE"]].map(([l, v]) => (
              <div key={l} className="live-stat"><div className="live-stat__v">{v}</div><div className="live-stat__l">{l}</div></div>
            ))}
          </div>
        </div>

        {/* Right: Stats */}
        <div className="hud-panel stats-panel anim-slide-right">
          <div style={{ fontFamily:"var(--f-hud)", fontSize:"0.6rem", color:"rgba(255,255,255,0.4)", letterSpacing:"3px", borderBottom:"1px solid rgba(255,255,255,0.08)", paddingBottom:"0.5rem", marginBottom:"0.5rem" }}>
            PLAYER STATS
          </div>
          {[["RANK","#A+","#00d4ff"],["WINS","31","#10b981"],["STREAK","7 🔥","#f59e0b"],["BADGES","12","#9333ea"]].map(([l, v, c]) => (
            <div key={l} className="stat-item">
              <div className="stat-item__label">{l}</div>
              <div className="stat-item__value" style={{ color:c }}>{v}</div>
            </div>
          ))}
          <div style={{ marginTop:"0.8rem", padding:"0.6rem", background:"rgba(0,212,255,0.05)", border:"1px solid rgba(0,212,255,0.15)", borderRadius:"5px" }}>
            <div style={{ fontFamily:"var(--f-mono)", fontSize:"0.55rem", color:"rgba(255,255,255,0.35)", letterSpacing:"2px", marginBottom:"3px" }}>ACTIVE WEAPON</div>
            <div style={{ fontFamily:"var(--f-hud)", fontSize:"0.7rem", color:"#00d4ff" }}>⚡ JS BLADE</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SCREEN: LANGUAGE SELECT
═══════════════════════════════════════════════════════════════ */
function LanguageScreen({ selected, onSelect, onConfirm, onBack }) {
  return (
    <div className="screen anim-fade">
      <div className="lang-screen-wrap">
        <div className="lang-screen-top">
          {/* Left char */}
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"1rem" }}>
            <PlayerCharacter size={140} color="#9333ea" label="SELECT WEAPON" animate/>
            <div style={{ fontFamily:"var(--f-mono)", fontSize:"0.65rem", color:"rgba(255,255,255,0.4)", letterSpacing:"2px", textAlign:"center" }}>
              CHOOSE YOUR<br/>BATTLE LANGUAGE
            </div>
          </div>
          {/* Title */}
          <div>
            <div style={{ color:"#00d4ff", fontFamily:"var(--f-hud)", fontSize:"clamp(1rem,3vw,1.6rem)", fontWeight:900, letterSpacing:"4px", marginBottom:"0.3rem" }}>SELECT LANGUAGE</div>
            <div style={{ color:"rgba(255,255,255,0.35)", fontFamily:"var(--f-mono)", fontSize:"0.68rem", letterSpacing:"3px", marginBottom:"1rem" }}>CHOOSE YOUR BATTLE WEAPON</div>
            <div style={{ fontFamily:"var(--f-mono)", fontSize:"0.62rem", color:"rgba(0,212,255,0.6)", letterSpacing:"2px" }}>
              ◈ Click a language → then choose a topic to practice
            </div>
          </div>
        </div>

        {/* Language grid — 4 columns, 2 rows = 7 langs + 1 back */}
        <div className="lang-grid-full">
          {LANGUAGES.map((lang, i) => {
            const isSel = selected?.id === lang.id;
            return (
              <div key={lang.id} className="lang-card" onClick={() => onSelect(lang)} style={{
                background:"linear-gradient(135deg,rgba(0,0,0,0.88),rgba(0,0,0,0.6))",
                border:`2px solid ${isSel ? lang.color : lang.color + "44"}`,
                boxShadow: isSel ? `0 0 28px ${lang.color}55,inset 0 0 18px ${lang.color}0d` : `0 0 12px ${lang.color}18`,
                animationDelay:`${i * 0.08}s`,
              }}>
                {isSel && <div className="lang-card__sel-badge" style={{ background:lang.color }}>SELECTED</div>}
                <div className="lang-card__icon">{lang.icon}</div>
                <div className="lang-card__name" style={{ color:lang.color }}>{lang.name}</div>
                <div className="lang-card__desc">{lang.desc}</div>
              </div>
            );
          })}
        </div>

        <div style={{ display:"flex", gap:"0.8rem" }}>
          <Btn onClick={onBack} color="purple">← BACK</Btn>
          {selected && <Btn onClick={onConfirm} color="green">SELECT TOPIC →</Btn>}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SCREEN: TOPIC SELECT
═══════════════════════════════════════════════════════════════ */
function TopicScreen({ lang, onSelect, onBack }) {
  const topics = TOPICS[lang.id] || [];
  return (
    <div className="screen anim-fade">
      <div className="topic-screen">
        <div className="topic-header">
          <div className="topic-header__title">
            {lang.icon} {lang.name} — SELECT TOPIC
          </div>
          <div className="topic-header__sub">WHAT DO YOU WANT TO PRACTICE TODAY?</div>
        </div>

        {/* Character + topic grid side by side */}
        <div style={{ display:"grid", gridTemplateColumns:"150px 1fr", gap:"2rem", width:"100%", alignItems:"start" }}>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"0.5rem" }}>
            <PlayerCharacter size={130} color={lang.color} label={lang.name} animate/>
            <div style={{ fontFamily:"var(--f-hud)", fontSize:"0.58rem", color:lang.color, letterSpacing:"2px", textAlign:"center", textShadow:`0 0 8px ${lang.color}` }}>
              CHOOSE<br/>YOUR PATH
            </div>
          </div>
          <div className="topic-grid">
            {topics.map((topic, i) => (
              <div key={topic.id} className="topic-card" style={{ animationDelay:`${i * 0.07}s` }}
                onClick={() => onSelect(topic)}>
                <span className="topic-card__icon">{topic.icon}</span>
                <div>
                  <div className="topic-card__name">{topic.name}</div>
                  <div className="topic-card__desc">{topic.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Btn onClick={onBack} color="purple">← BACK</Btn>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SCREEN: GAME (QUIZ HUD)
═══════════════════════════════════════════════════════════════ */
function GameScreen({ onFinish, player, lang, topic }) {
  const questions = getQuestions(lang.id, topic.id);
  const [qIdx,     setQIdx]     = useState(0);
  const [score,    setScore]    = useState(0);
  const [chosen,   setChosen]   = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [showPts,  setShowPts]  = useState(false);
  const [hp,       setHp]       = useState(100);
  const [xp,       setXp]       = useState(player.xp);

  const q     = questions[qIdx];
  const total = questions.length;
  const timer = useCountdown(chosen === null ? 20 : 0, () => { if (chosen === null) handleAnswer(-1); });

  function handleAnswer(idx) {
    if (chosen !== null) return;
    setChosen(idx);
    if (idx === q.ans) {
      setScore(s => s + 10); setXp(x => Math.min(100, x + 8));
      setFeedback("correct"); setShowPts(true);
      setTimeout(() => setShowPts(false), 1200);
      setTimeout(() => {
        if (qIdx + 1 >= total) { onFinish(score + 10); return; }
        setQIdx(i => i + 1); setChosen(null); setFeedback(null);
      }, 1500);
    } else {
      setHp(h => Math.max(0, h - 25)); setFeedback("wrong");
      setTimeout(() => onFinish(score), 1500);
    }
  }

  const timerCls  = timer > 10 ? "hud-timer--ok" : timer > 5 ? "hud-timer--warn" : "hud-timer--danger";
  const cardCls   = feedback === "correct" ? "q-card--correct" : feedback === "wrong" ? "q-card--wrong" : "";
  const charColor = feedback === "wrong" ? "#ef4444" : feedback === "correct" ? "#10b981" : lang.color;
  const optColors = ["0,212,255", "147,51,234", "16,185,129", "240,219,79"];

  return (
    <div className="screen anim-fade" style={{ justifyContent:"flex-start", paddingTop:"1rem" }}>
      <div className="game-hud">
        {/* Top HUD */}
        <div className="hud-panel hud-top">
          <div className="hud-q-badge">Q {qIdx + 1}/{total}</div>
          <div className="hud-lang-badge">{lang.icon} {lang.name} · {topic.name}</div>
          <div className="hud-progress-track">
            <div className="hud-progress-fill" style={{ width:`${((qIdx + 1) / total) * 100}%` }}/>
          </div>
          <div className="hud-score-pill">
            ⭐ {score}
            {showPts && <div className="score-float-anim">+10 PTS!</div>}
          </div>
          <div className={`hud-timer ${timerCls}`}>{timer}</div>
        </div>

        {/* Left Player HUD */}
        <div className="hud-panel player-hud anim-slide-left">
          <PlayerCharacter size={110} color={charColor} label={player.name} animate/>
          <div className="player-hud__name">{player.name}</div>
          <div className="player-hud__score">⭐ {score}</div>
          <StatBar label="HP" value={hp} colorCls="stat-bar-fill--hp" max={100}/>
          <StatBar label="XP" value={xp} colorCls="stat-bar-fill--xp" max={100}/>
          {feedback === "wrong"   && <div style={{ color:"var(--c-red)",   fontFamily:"var(--f-hud)", fontSize:"0.58rem", textAlign:"center", marginTop:"0.3rem" }}>-25 HP</div>}
          {feedback === "correct" && <div style={{ color:"var(--c-green)", fontFamily:"var(--f-hud)", fontSize:"0.58rem", textAlign:"center", marginTop:"0.3rem" }}>+8 XP ✓</div>}
        </div>

        {/* Center Question */}
        <div className={`hud-panel q-card ${cardCls}`} style={{ background:"linear-gradient(135deg,rgba(0,0,0,0.92),rgba(0,212,255,0.04))" }}>
          <div className="q-lang-tag">◈ {lang.name} — {topic.name} ◈</div>
          <div className="q-text">{q.q}</div>
          <div className="code-snip">
            <span className="code-snip__hdr">CODE SNIPPET</span>
            {q.code}
          </div>
          <div className="opts-grid">
            {q.opts.map((opt, i) => {
              let cls = "opt-btn";
              if (chosen !== null) {
                if (i === q.ans) cls += " opt-btn--correct";
                else if (i === chosen) cls += " opt-btn--wrong";
              }
              return (
                <button key={i} className={cls} onClick={() => handleAnswer(i)} disabled={chosen !== null}>
                  <span className="opt-letter" style={{
                    background:`rgba(${optColors[i]},0.14)`,
                    border:`1px solid rgba(${optColors[i]},0.5)`,
                    color:`rgb(${optColors[i]})`,
                  }}>{["A","B","C","D"][i]}</span>
                  {opt}
                </button>
              );
            })}
          </div>
          {feedback === "wrong" && <div className="wrong-msg">✗ WRONG — GAME OVER</div>}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SCREEN: LEADERBOARD (with Battle Request)
═══════════════════════════════════════════════════════════════ */
function LeaderboardScreen({ onBack, player, onBattleStart }) {
  const [sentTo,       setSentTo]       = useState(null);   // student we sent request to
  const [modalStudent, setModalStudent] = useState(null);   // student selected to challenge
  const [acceptModal,  setAcceptModal]  = useState(false);  // show incoming request

  const rowCls   = ["lb-row--gold", "lb-row--silver", "lb-row--bronze"];
  const medalClr = ["#FFD700", "#C0C0C0", "#CD7F32"];

  function openChallenge(student) {
    setModalStudent(student);
  }

  function sendRequest() {
    setSentTo(modalStudent);
    setModalStudent(null);
    // Simulate: after 3s opponent "accepts"
    setTimeout(() => {
      setAcceptModal(true);
    }, 3000);
  }

  function acceptBattle() {
    setAcceptModal(false);
    onBattleStart(sentTo);
  }

  return (
    <div className="screen anim-fade">
      {/* Challenge Request Modal */}
      {modalStudent && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-box__title">⚔ SEND BATTLE REQUEST</div>
            <div className="modal-box__sub">CHALLENGE THIS PLAYER TO A 1v1</div>
            <div className="modal-box__target">
              <div className="modal-box__avatar">{modalStudent.avatar}</div>
              <div>
                <div className="modal-box__name">{modalStudent.name}</div>
                <div className="modal-box__pts">⭐ {modalStudent.pts.toLocaleString()} pts · {modalStudent.city}</div>
                <div style={{ fontFamily:"var(--f-mono)", fontSize:"0.58rem", color: modalStudent.online ? "#10b981" : "#ef4444", marginTop:"3px" }}>
                  {modalStudent.online ? "🟢 ONLINE" : "🔴 OFFLINE"}
                </div>
              </div>
            </div>
            <div className="modal-box__btns">
              <Btn onClick={sendRequest} color="orange">SEND REQUEST ⚡</Btn>
              <Btn onClick={() => setModalStudent(null)} color="purple">CANCEL</Btn>
            </div>
          </div>
        </div>
      )}

      {/* Accept / Waiting Modal */}
      {acceptModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="accept-ping"><span className="accept-ping__icon">⚡</span></div>
            <div className="modal-box__title" style={{ color:"#10b981" }}>REQUEST ACCEPTED!</div>
            <div className="modal-box__sub">{sentTo?.name} ACCEPTED YOUR CHALLENGE</div>
            <div className="modal-box__target">
              <div className="modal-box__avatar">{sentTo?.avatar}</div>
              <div>
                <div className="modal-box__name">{sentTo?.name}</div>
                <div className="modal-box__pts">Ready to battle!</div>
              </div>
            </div>
            <div className="modal-box__btns">
              <Btn onClick={acceptBattle} color="green" size="lg">START BATTLE ⚔</Btn>
            </div>
          </div>
        </div>
      )}

      {/* Waiting for response */}
      {sentTo && !acceptModal && !modalStudent && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="battle-waiting">
              <div className="battle-waiting__label">WAITING FOR RESPONSE...</div>
              <div style={{ color:"rgba(255,255,255,0.5)", fontFamily:"var(--f-mono)", fontSize:"0.65rem", marginBottom:"1rem" }}>
                Request sent to {sentTo.name}
              </div>
              <div className="battle-dots">
                <div className="battle-dot"/><div className="battle-dot"/><div className="battle-dot"/>
              </div>
            </div>
            <div style={{ marginTop:"1rem" }}>
              <Btn onClick={() => setSentTo(null)} color="red" size="sm">CANCEL REQUEST</Btn>
            </div>
          </div>
        </div>
      )}

      <div className="lb-layout">
        {/* Left char */}
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"0.8rem" }}>
          <PlayerCharacter size={130} color="#f0db4f" label={player.name} animate/>
          <div style={{ fontFamily:"var(--f-hud)", fontSize:"0.6rem", color:"#f0db4f", letterSpacing:"2px", textAlign:"center" }}>
            YOUR RANK<br/>#4
          </div>
          <div style={{ fontFamily:"var(--f-mono)", fontSize:"0.55rem", color:"rgba(255,255,255,0.4)", textAlign:"center", lineHeight:1.6 }}>
            Click ⚔ next to any<br/>player to challenge them
          </div>
        </div>

        {/* Right: list */}
        <div>
          <div style={{ color:"#f0db4f", fontFamily:"var(--f-hud)", fontSize:"clamp(1rem,3vw,1.6rem)", fontWeight:900, letterSpacing:"4px", marginBottom:"0.3rem" }}>
            🏆 LEADERBOARD
          </div>
          <div style={{ color:"rgba(255,255,255,0.35)", fontFamily:"var(--f-mono)", fontSize:"0.65rem", marginBottom:"1.2rem", letterSpacing:"3px" }}>
            REGISTERED ARENA PLAYERS THIS WEEK
          </div>
          <div className="lb-list">
            {LEADERBOARD_STUDENTS.map((p, i) => (
              <div key={p.rank} className={`lb-row ${i < 3 ? rowCls[i] : "lb-row--normal"}`}>
                <div className="lb-badge" style={{
                  background: i<3 ? `radial-gradient(circle,${medalClr[i]}33,transparent)` : "rgba(255,255,255,0.05)",
                  border:`2px solid ${i<3 ? medalClr[i] : "rgba(255,255,255,0.12)"}`,
                  color: i<3 ? medalClr[i] : "rgba(255,255,255,0.4)",
                }}>{p.badge || p.rank}</div>
                <div className="lb-avatar">{p.avatar}</div>
                <div style={{ flex:1 }}>
                  <div className="lb-name" style={{ color:i<3 ? medalClr[i] : "#fff" }}>
                    {p.name}
                    <span style={{ marginLeft:"6px", fontSize:"0.55rem", color: p.online ? "#10b981" : "rgba(255,255,255,0.2)" }}>
                      {p.online ? "🟢" : "⚫"}
                    </span>
                  </div>
                  <div className="lb-wins">{p.wins} WINS · {p.city}</div>
                </div>
                <div className="lb-pts" style={{ color:i<3 ? medalClr[i] : "#00d4ff", textShadow:`0 0 8px ${i<3 ? medalClr[i] : "#00d4ff"}` }}>
                  {p.pts.toLocaleString()}
                </div>
                <button
                  className={`lb-challenge-btn ${sentTo?.rank === p.rank ? "lb-challenge-btn--sent" : ""}`}
                  onClick={() => !sentTo && openChallenge(p)}
                  disabled={!!sentTo}
                >
                  {sentTo?.rank === p.rank ? "✓ SENT" : "⚔ BATTLE"}
                </button>
              </div>
            ))}
          </div>
          <div style={{ marginTop:"1.5rem" }}>
            <Btn onClick={onBack} color="purple">← BACK TO LOBBY</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SCREEN: DAILY CHALLENGE
═══════════════════════════════════════════════════════════════ */
function DailyScreen({ onBack, player }) {
  const [h, setH] = useState(14); const [m, setM] = useState(23); const [s, setS] = useState(47);
  useEffect(() => {
    const iv = setInterval(() => {
      setS(ps => {
        if (ps > 0) return ps - 1;
        setM(pm => { if (pm > 0) return pm - 1; setH(ph => ph > 0 ? ph - 1 : 0); return pm > 0 ? 59 : 0; });
        return 59;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, []);
  const diff    = DAILY.difficulty;
  const diffCls = diff === "EASY" ? "diff-badge--easy" : diff === "MEDIUM" ? "diff-badge--medium" : "diff-badge--hard";

  return (
    <div className="screen anim-fade">
      <div className="daily-layout">
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"0.8rem" }}>
          <PlayerCharacter size={140} color="#f59e0b" label={player.name} animate/>
        </div>
        <div>
          <div style={{ color:"#f59e0b", fontFamily:"var(--f-hud)", fontSize:"clamp(1rem,3vw,1.6rem)", fontWeight:900, letterSpacing:"4px", marginBottom:"1.5rem" }}>
            ☀ DAILY CHALLENGE
          </div>
          <div className="hud-panel daily-card">
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1rem" }}>
              <span className={`diff-badge ${diffCls}`}>{diff}</span>
              <span className="daily-reward">🎁 +{DAILY.reward} PTS</span>
            </div>
            <div className="daily-title">{DAILY.title}</div>
            <div className="daily-desc">{DAILY.desc}</div>
            <div className="clock-wrap">
              <div className="clock-label">NEXT CHALLENGE IN</div>
              <div className="clock-row">
                {[[h,"HRS"],[m,"MIN"],[s,"SEC"]].map(([v, l]) => (
                  <div key={l} className="clock-digit">
                    <div className="clock-digit__v">{String(v).padStart(2,"0")}</div>
                    <div className="clock-digit__l">{l}</div>
                  </div>
                ))}
              </div>
            </div>
            <Btn onClick={onBack} color="orange" size="lg" full>ACCEPT CHALLENGE →</Btn>
          </div>
          <div style={{ marginTop:"1rem" }}><Btn onClick={onBack} color="purple">← BACK</Btn></div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SCREEN: 1v1 BATTLE (after request accepted)
═══════════════════════════════════════════════════════════════ */
function BattleScreen({ onBack, player, opponent }) {
  const [phase,   setPhase]   = useState("fighting");
  const [p1hp,    setP1hp]    = useState(100);
  const [p2hp,    setP2hp]    = useState(100);
  const [chosen,  setChosen]  = useState(null);
  const [winner,  setWinner]  = useState(null);

  const questions = getQuestions("js", "basics");
  const q         = questions[0];
  const optColors = ["0,212,255", "147,51,234", "16,185,129", "240,219,79"];

  useEffect(() => {
    if (phase !== "fighting" || chosen !== null) return;
    const t = setTimeout(() => {
      setP1hp(h => Math.max(0, h - 40)); setWinner("BOT"); setPhase("result");
    }, 9000);
    return () => clearTimeout(t);
  }, [phase, chosen]);

  function handleAnswer(idx) {
    if (chosen !== null || phase !== "fighting") return;
    setChosen(idx);
    if (idx === q.ans) { setP2hp(h => Math.max(0, h - 40)); setWinner("YOU"); }
    else               { setP1hp(h => Math.max(0, h - 40)); setWinner("OPP"); }
    setPhase("result");
  }

  const opp = opponent || { name:"OPPONENT", avatar:"🤖", color:"#ef4444" };

  return (
    <div className="screen anim-fade" style={{ justifyContent:"flex-start", paddingTop:"1.5rem" }}>
      <div className="battle-layout">
        <div style={{ textAlign:"center", marginBottom:"0.5rem" }}>
          <div style={{ color:"#9333ea", fontFamily:"var(--f-hud)", fontSize:"clamp(0.9rem,2.5vw,1.4rem)", fontWeight:900, letterSpacing:"4px" }}>
            ⚔ 1 VS 1 CODING BATTLE
          </div>
          <div style={{ fontFamily:"var(--f-mono)", fontSize:"0.6rem", color:"rgba(255,255,255,0.4)", letterSpacing:"2px", marginTop:"3px" }}>
            {opp.name} VS {player.name}
          </div>
        </div>

        {/* Characters + HP */}
        <div style={{ display:"flex", alignItems:"flex-end", gap:"1rem", marginBottom:"0.5rem" }}>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", flex:"0 0 auto" }}>
            <PlayerCharacter size={100} color="#ef4444" label={opp.name} animate={phase === "fighting"} showLabel/>
          </div>
          <div className="hud-panel" style={{ flex:1, padding:"0.8rem 1rem", display:"flex", flexDirection:"column", gap:"0.8rem" }}>
            <div className="battle-player-block">
              <div className="battle-player-header">
                <span className="battle-player-avatar">{opp.avatar}</span>
                <span className="battle-player-name" style={{ color:"#ef4444" }}>{opp.name}</span>
                <span style={{ marginLeft:"auto", fontFamily:"var(--f-hud)", fontSize:"0.7rem", color:"#ef4444" }}>{p1hp}%</span>
              </div>
              <div className="battle-hp-track"><div className="battle-hp-fill battle-hp-fill--p1" style={{ width:`${p1hp}%` }}/></div>
            </div>
            <div style={{ textAlign:"center", fontFamily:"var(--f-hud)", fontWeight:900, fontSize:"0.95rem", color:"#fff" }}>VS</div>
            <div className="battle-player-block">
              <div className="battle-player-header">
                <span className="battle-player-avatar">👾</span>
                <span className="battle-player-name" style={{ color:"#00d4ff" }}>{player.name}</span>
                <span style={{ marginLeft:"auto", fontFamily:"var(--f-hud)", fontSize:"0.7rem", color:"#00d4ff" }}>{p2hp}%</span>
              </div>
              <div className="battle-hp-track"><div className="battle-hp-fill battle-hp-fill--p2" style={{ width:`${p2hp}%` }}/></div>
            </div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", flex:"0 0 auto" }}>
            <PlayerCharacter size={100} color="#00d4ff" label={player.name} animate={phase === "fighting"} showLabel/>
          </div>
        </div>

        {/* Question */}
        {phase === "fighting" && (
          <div className="hud-panel anim-slide-up" style={{ padding:"1.4rem", background:"linear-gradient(135deg,rgba(0,0,0,0.92),rgba(147,51,234,0.04))" }}>
            <div style={{ color:"#9333ea", fontFamily:"var(--f-mono)", fontSize:"0.58rem", letterSpacing:"3px", marginBottom:"0.7rem" }}>
              ◈ SAME QUESTION — FIRST CORRECT ANSWER WINS ◈
            </div>
            <div style={{ color:"#fff", fontFamily:"var(--f-body)", fontSize:"1rem", fontWeight:600, marginBottom:"0.9rem" }}>{q.q}</div>
            <div className="code-snip" style={{ marginBottom:"1rem" }}>{q.code}</div>
            <div className="opts-grid">
              {q.opts.map((opt, i) => (
                <button key={i} className="opt-btn" onClick={() => handleAnswer(i)}>
                  <span className="opt-letter" style={{ background:`rgba(${optColors[i]},0.14)`, border:`1px solid rgba(${optColors[i]},0.5)`, color:`rgb(${optColors[i]})` }}>
                    {["A","B","C","D"][i]}
                  </span>
                  {opt}
                </button>
              ))}
            </div>
            <div className="battle-note">⚡ Answer faster than your opponent to deal damage!</div>
          </div>
        )}

        {phase === "result" && (
          <div className="hud-panel battle-result">
            <div className={`battle-result__title ${winner === "YOU" ? "battle-result__win" : "battle-result__lose"}`}>
              {winner === "YOU" ? "🏆 YOU WIN!" : "💀 YOU LOSE!"}
            </div>
            <div style={{ display:"flex", gap:"0.8rem", justifyContent:"center" }}>
              <Btn onClick={onBack} color="blue">BACK TO LOBBY</Btn>
            </div>
          </div>
        )}

        {phase !== "result" && (
          <div style={{ marginTop:"0.8rem", textAlign:"center" }}>
            <Btn onClick={onBack} color="red" size="sm">FORFEIT</Btn>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SCREEN: GAME OVER
═══════════════════════════════════════════════════════════════ */
function GameOverScreen({ score, onReplay, onLobby, player, lang, topic }) {
  const questions = getQuestions(lang?.id || "js", topic?.id || "basics");
  const rank      = score >= 40 ? "S" : score >= 30 ? "A" : score >= 20 ? "B" : score >= 10 ? "C" : "D";
  const rankHex   = { S:"#FFD700", A:"#00d4ff", B:"#10b981", C:"#f59e0b", D:"#ef4444" };
  const rc        = rankHex[rank];
  const charColor = rank === "S" || rank === "A" ? "#10b981" : "#ef4444";

  return (
    <div className="screen anim-fade">
      <div className="go-layout">
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"0.8rem" }}>
          <PlayerCharacter size={150} color={charColor} label={player.name} animate/>
          <div style={{ fontFamily:"var(--f-hud)", fontSize:"0.58rem", color:charColor, letterSpacing:"2px", textAlign:"center", textShadow:`0 0 8px ${charColor}` }}>
            {rank === "S" || rank === "A" ? "VICTORY!" : "DEFEATED"}
          </div>
        </div>
        <div className="hud-panel go-card">
          <div className="go-title">GAME OVER</div>
          <div className="go-sub">SESSION COMPLETE</div>
          <div className="rank-circle" style={{ background:`radial-gradient(circle,${rc}22,transparent)`, borderColor:rc, boxShadow:`0 0 28px ${rc}55` }}>
            <span className="rank-circle__letter" style={{ color:rc, textShadow:`0 0 12px ${rc}` }}>{rank}</span>
          </div>
          <div className="go-score-label">FINAL SCORE</div>
          <div className="go-score-val">{score}</div>
          <div className="go-stats">
            {[
              ["RANK", rank, rc],
              ["CORRECT", `${Math.floor(score/10)}/${questions.length}`, "#00d4ff"],
              ["XP GAINED", `+${score*2}`, "#10b981"],
            ].map(([l, v, c]) => (
              <div key={l}>
                <div className="go-stat__v" style={{ color:c }}>{v}</div>
                <div className="go-stat__l">{l}</div>
              </div>
            ))}
          </div>
          <div style={{ display:"flex", gap:"0.8rem", justifyContent:"center" }}>
            <Btn onClick={onReplay} color="green" size="lg">▶ PLAY AGAIN</Btn>
            <Btn onClick={onLobby}  color="purple" size="lg">⌂ LOBBY</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ROOT APP
═══════════════════════════════════════════════════════════════ */
export default function CodeArena2() {
  const [screen,      setScreen]     = useState("lobby");
  const [selectedLang, setLang]      = useState(null);
  const [selectedTopic, setTopic]    = useState(null);
  const [finalScore,  setFinalScore] = useState(0);
  const [battleOpp,   setBattleOpp]  = useState(null);
  const [playerData]                 = useState({ ...PLAYER });
  const nav = s => setScreen(s);

  return (
    <div className="arena-root">
      <ParticleField/>
      <div className="arena-scanlines"/>
      <div className="arena-content">

        {screen === "lobby" && (
          <LobbyScreen onNav={nav} player={playerData}/>
        )}

        {screen === "language" && (
          <LanguageScreen
            selected={selectedLang}
            onSelect={setLang}
            onConfirm={() => { if (selectedLang) nav("topic"); }}
            onBack={() => nav("lobby")}
          />
        )}

        {screen === "topic" && selectedLang && (
          <TopicScreen
            lang={selectedLang}
            onSelect={t => { setTopic(t); nav("game"); }}
            onBack={() => nav("language")}
          />
        )}

        {screen === "game" && selectedLang && selectedTopic && (
          <GameScreen
            onFinish={s => { setFinalScore(s); nav("gameover"); }}
            player={playerData}
            lang={selectedLang}
            topic={selectedTopic}
          />
        )}

        {screen === "leaderboard" && (
          <LeaderboardScreen
            onBack={() => nav("lobby")}
            player={playerData}
            onBattleStart={opp => { setBattleOpp(opp); nav("battle"); }}
          />
        )}

        {screen === "daily" && (
          <DailyScreen onBack={() => nav("lobby")} player={playerData}/>
        )}

        {screen === "battle" && (
          <BattleScreen
            onBack={() => nav("lobby")}
            player={playerData}
            opponent={battleOpp}
          />
        )}

        {screen === "gameover" && (
          <GameOverScreen
            score={finalScore}
            onReplay={() => nav("game")}
            onLobby={() => nav("lobby")}
            player={playerData}
            lang={selectedLang}
            topic={selectedTopic}
          />
        )}

      </div>
    </div>
  );
}